import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, FileText, Download, ShieldOff, RotateCcw, FileSignature, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Pager } from "@/components/Pager";
import { RefreshButton } from "@/components/RefreshButton";

const errMsg = (e) => formatApiError(e.response?.data?.detail);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const FILTROS = [["", "Todos"], ["signed", "Assinados"], ["pending_account", "Pendentes (com conta)"], ["pending_noaccount", "Pendentes (sem conta ainda)"], ["waived", "Dispensados"]];
const BADGE = { assinado: "bg-green-50 text-green-700", pendente: "bg-amber-50 text-amber-700", aguardando: "bg-sky-50 text-sky-700", dispensado: "bg-slate-100 text-slate-600" };

/** Quem já assinou o contrato digital, quem falta e a cópia assinada de cada aluno. */
export default function Contracts() {
  const [sum, setSum] = useState(null);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const loadSum = useCallback(() => api.get("/admin/contracts/summary").then((r) => setSum(r.data)).catch(() => {}), []);
  const load = useCallback(() => api.get("/admin/contracts", { params: { status, q: query, page, limit: 50 } }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ total: 0, rows: [] }); }), [status, query, page]);
  useEffect(() => { loadSum(); }, [loadSum]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setQuery(q); }, 350); return () => clearTimeout(t); }, [q]);

  const baixar = async (rota, nome) => {
    try { const r = await api.get(rota, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dispensar = async (r) => {
    const motivo = window.prompt(`Dispensar a assinatura de ${r.name}?\n\nExplique o motivo (fica registrado com o seu nome):`);
    if (!motivo) return;
    try { await api.post(`/admin/contracts/${r.id}/waive`, { reason: motivo }); toast.success("Dispensa registrada."); load(); loadSum(); } catch (e) { toast.error(errMsg(e)); }
  };
  const desfazer = async (r) => { try { await api.post(`/admin/contracts/${r.id}/unwaive`); toast.success("Dispensa desfeita."); load(); loadSum(); } catch (e) { toast.error(errMsg(e)); } };

  const card = (rotulo, valor, cls, filtro) => (
    <button type="button" onClick={() => { setStatus(filtro); setPage(1); }} className={`text-left rounded-xl border p-4 bg-white hover:shadow-sm transition ${status === filtro ? "border-[#1E9E2A] ring-1 ring-[#1E9E2A]/30" : "border-slate-200"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{rotulo}</div><div className={`font-display text-2xl font-bold mt-1 ${cls}`}>{valor ?? "…"}</div>
    </button>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><FileSignature className="h-7 w-7 text-[#1E9E2A]" /> Contratos assinados</h1>
          <p className="text-slate-500 mt-1">Acompanhe quem já assinou a versão atual do contrato (v{sum?.version ?? "…"}) e baixe a cópia assinada de cada aluno.</p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton onClick={() => Promise.all([load(), loadSum()])} variant="outline" />
          <Link to="/admin/escola#contrato"><Button variant="outline"><Settings2 className="h-4 w-4 mr-1.5" /> Configurar</Button></Link>
        </div>
      </div>

      {sum && !sum.required && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
          A assinatura digital está <strong>desligada</strong>: os alunos ainda não são obrigados a assinar. Ligue em <Link to="/admin/escola#contrato" className="underline font-semibold">Configurações → Escola → Contrato</Link> quando o texto estiver revisado.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {card("Assinaram", sum?.signed, "text-green-700", "signed")}
        {card("Pendentes (com conta)", sum?.pending_account, "text-amber-700", "pending_account")}
        {card("Pendentes (sem conta ainda)", sum?.pending_noaccount, "text-slate-600", "pending_noaccount")}
        {card("Dispensados", sum?.waived, "text-slate-500", "waived")}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno por nome ou e-mail..." className="pl-9 h-10" data-testid="contracts-search" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          {FILTROS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {status && status !== "signed" && <Button variant="outline" onClick={() => baixar(`/admin/contracts-export?status=${status === "waived" ? "waived" : status}`, `contratos-${status}.csv`)}><Download className="h-4 w-4 mr-1.5" /> Baixar lista (CSV)</Button>}
        <Button variant="ghost" onClick={() => baixar("/admin/contracts-preview-pdf", "contrato-modelo.pdf")}><FileText className="h-4 w-4 mr-1.5" /> Ver contrato de exemplo</Button>
      </div>

      {data === null ? <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto iep-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
              <th className="px-4 py-3">Aluno</th><th className="px-3 py-3">Situação</th><th className="px-3 py-3 hidden md:table-cell">Assinou em</th><th className="px-3 py-3 hidden lg:table-cell">Código</th><th className="px-4 py-3 text-right">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nenhum aluno encontrado.</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id} data-testid={`contract-row-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.email}{!r.has_account && r.status === "pendente" ? " · ainda sem conta" : ""}</div></td>
                  <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BADGE[r.status]}`}>{r.status === "assinado" ? "Assinado" : r.status === "dispensado" ? "Dispensado" : r.status === "aguardando" ? "Aguardando assinatura" : "Pendente"}</span></td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-slate-600">{fmt(r.contract_signed_at)}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell font-mono text-xs text-slate-500">{r.contract_code || "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.status === "assinado" && r.contract_signature_id && <Button size="sm" variant="outline" onClick={() => baixar(`/admin/contracts/${r.contract_signature_id}/pdf`, `contrato-${r.contract_code}.pdf`)}><FileText className="h-3.5 w-3.5 mr-1" /> Contrato (PDF)</Button>}
                    {(r.status === "pendente" || r.status === "aguardando") && <Button size="sm" variant="ghost" onClick={() => dispensar(r)} title="Libera o acesso sem assinatura (caso especial)"><ShieldOff className="h-3.5 w-3.5 mr-1" /> Dispensar</Button>}
                    {r.status === "dispensado" && <Button size="sm" variant="ghost" onClick={() => desfazer(r)} title={r.contract_waive_reason}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Desfazer</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} perPage={50} total={data.total} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
